import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { Search, Smartphone, RotateCcw, Shield, AlertTriangle } from "lucide-react";
import { getAllUsersWithDevices, resetUserDeviceByEmployeeId } from "../../services/firebaseService";
import Toast from "../Common/Toast";
import "../../Styles/Admin/DeviceManagement.css";

const DeviceManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [resetStatus, setResetStatus] = useState({});
  const [toast, setToast] = useState({ message: "", type: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsersWithDevices();
  }, []);

  const fetchUsersWithDevices = async () => {
    try {
      setLoading(true);
      console.log('🔧 DeviceManagement: Fetching users with devices...');
      const usersWithDevices = await getAllUsersWithDevices();
      
      // Convert to array and filter only normal users
      const userArray = Object.entries(usersWithDevices)
        .map(([firebaseUid, userData]) => ({
          firebaseUid,
          ...userData
        }))
        .filter(user => user.role === 'user'); // Only show normal users
      
      console.log('🔧 DeviceManagement: Filtered users:', userArray);
      setUsers(userArray);
      setFilteredUsers(userArray);
    } catch (error) {
      console.error('❌ DeviceManagement: Error fetching users:', error);
      setToast({ message: "Failed to load user devices", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = users.filter(
      (user) => 
        user.name?.toLowerCase().includes(term) || 
        user.email?.toLowerCase().includes(term) ||
        user.employeeId?.toLowerCase().includes(term)
    );
    setFilteredUsers(filtered);
  };

  const handleDeviceReset = async (user) => {
    if (!user.employeeId) {
      setToast({ message: "Employee ID not found for this user", type: "error" });
      return;
    }

    try {
      setResetStatus({ ...resetStatus, [user.firebaseUid]: { loading: true } });
      
      console.log('🔧 DeviceManagement: Resetting device for:', user.name, user.employeeId);
      const success = await resetUserDeviceByEmployeeId(user.employeeId);
      
      if (success) {
        setResetStatus({ ...resetStatus, [user.firebaseUid]: { success: true } });
        setToast({ 
          message: `Device reset successful for ${user.name}`, 
          type: "success" 
        });
        
        // Refresh the data
        setTimeout(() => {
          fetchUsersWithDevices();
          setResetStatus({ ...resetStatus, [user.firebaseUid]: null });
        }, 2000);
      } else {
        setResetStatus({ ...resetStatus, [user.firebaseUid]: { error: 'Failed to reset device' } });
        setToast({ 
          message: `Failed to reset device for ${user.name}`, 
          type: "error" 
        });
      }
    } catch (error) {
      setResetStatus({ ...resetStatus, [user.firebaseUid]: { error: error.message } });
      setToast({ 
        message: `Error resetting device for ${user.name}: ${error.message}`, 
        type: "error" 
      });
    }
  };

  const getDeviceStatus = (user) => {
    if (!user.hasDevice || !user.deviceId) {
      return { 
        status: 'No Device', 
        color: 'gray', 
        icon: AlertTriangle,
        description: 'User has not logged in yet'
      };
    }
    if (user.resetAt && !user.deviceId) {
      return { 
        status: 'Reset', 
        color: 'orange', 
        icon: RotateCcw,
        description: 'Device was reset by admin'
      };
    }
    return { 
      status: 'Registered', 
      color: 'green', 
      icon: Shield,
      description: `Registered on ${new Date(user.registeredAt).toLocaleDateString()}`
    };
  };

  if (loading) {
    return (
      <div className="device-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading device information...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="device-management-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Toast
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ message: "", type: "" })}
      />
      
      <div className="device-management-header">
        <div className="header-content">
          <h2 className="page-title">
            <Smartphone className="title-icon" />
            Device Management
          </h2>
          <p className="page-description">
            Manage employee device registrations and security settings
          </p>
        </div>
        
        <div className="search-container">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            placeholder="Search by name, email, or employee ID..."
            className="search-input"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      <div className="device-stats">
        <div className="stat-card">
          <div className="stat-number">{users.filter(u => u.hasDevice).length}</div>
          <div className="stat-label">Registered Devices</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{users.filter(u => !u.hasDevice).length}</div>
          <div className="stat-label">Pending Registration</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{users.filter(u => u.resetAt).length}</div>
          <div className="stat-label">Recently Reset</div>
        </div>
      </div>

      <div className="device-table-container">
        <table className="device-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Employee ID</th>
              <th>Device Status</th>
              <th>Registration Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const deviceStatus = getDeviceStatus(user);
              const resetState = resetStatus[user.firebaseUid];
              const StatusIcon = deviceStatus.icon;
              
              return (
                <motion.tr
                  key={user.firebaseUid}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="device-table-row"
                >
                  <td>
                    <div className="employee-cell">
                      <div className="employee-avatar">
                        {user.name?.charAt(0) || 'U'}
                      </div>
                      <div className="employee-info">
                        <div className="employee-name">{user.name || 'Unknown'}</div>
                        <div className="employee-email">{user.email || 'No email'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="employee-id">{user.employeeId || 'N/A'}</span>
                  </td>
                  <td>
                    <div className="device-status-cell">
                      <div className={`device-status-badge status-${deviceStatus.color}`}>
                        <StatusIcon size={14} />
                        <span>{deviceStatus.status}</span>
                      </div>
                      <div className="device-status-description">
                        {deviceStatus.description}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="registration-date">
                      {user.registeredAt 
                        ? new Date(user.registeredAt).toLocaleDateString()
                        : 'Not registered'
                      }
                    </span>
                  </td>
                  <td>
                    <button
                      className={`reset-btn ${resetState?.loading ? 'loading' : ''} ${resetState?.success ? 'success' : ''}`}
                      onClick={() => handleDeviceReset(user)}
                      disabled={resetState?.loading}
                      title="Reset user's device registration"
                    >
                      <RotateCcw size={14} />
                      {resetState?.loading ? 'Resetting...' : 
                       resetState?.success ? 'Reset!' : 'Reset Device'}
                    </button>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="empty-state">
            <Smartphone size={48} className="empty-icon" />
            <h3>No Users Found</h3>
            <p>No employees match your search criteria.</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DeviceManagement; 